import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './auth';
import { homePathOf } from './routes';
import { useTranslation } from '@/i18n';
import type { UserRole } from '@/types/user';
import styles from './RequireAuth.module.css';

/**
 * Route guards.
 *
 * They mirror the two answers the server gives, and keep them distinct:
 *
 * - **401 / no session** → back to the login screen. Signing in again fixes it.
 * - **403 / wrong role** → stay signed in, back home: the board, or the map for a
 *   wall screen. Signing in again does NOT fix it, so bouncing to login would send
 *   the reader in a circle — home is the one screen their role does have.
 *
 * A guard is a courtesy, not the enforcement: the server checks every request
 * regardless. Its job is to avoid opening a screen whose every call will fail.
 *
 * No loading state: the session is read from storage during initialisation, so the
 * first render already knows the answer.
 */

export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Where they were heading, so login can continue the journey rather than
    // dropping them at the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const RequireRole = ({
  roles,
  children,
}: {
  roles: readonly UserRole[];
  children: ReactNode;
}) => {
  const { can, isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!can(roles)) {
    // Home is where a wrong-role reader belongs: the board for people — every screen
    // worth guarding is an administrator's, and everyone else works the board — and
    // the map for a wall screen, which may open nothing else. Except when home
    // ITSELF is what was refused (INTEGRATOR and OPERATOR are not on the online
    // table): redirecting there again would loop forever, so that one case says so
    // in place.
    const home = homePathOf(user);
    if (location.pathname === home) {
      return (
        <p className={styles.denied} role="alert">
          {t('auth.notPermitted')}
        </p>
      );
    }

    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
};
